/**
 * Foundation enums and basic types extracted to avoid circular dependencies.
 * @module
 */

export const NONE_VALUE = "NONE_VALUE";

// Foundation Super Systems

export enum FoundationSuperSystemType {
  SLAB_ON_GRADE = "SLAB_ON_GRADE",
  RAISED_SLAB = "RAISED_SLAB",
  CRAWLSPACE = "CRAWLSPACE",
  BASEMENT = "BASEMENT",
}

// Foundation Sub Systems

export enum FoundationSubSystemType {
  MONOLITHIC = "MONOLITHIC",
  STEM_WALL_CMU = "STEM_WALL_CMU",
  STEM_WALL_CIP = "STEM_WALL_CIP",
  ISOLATED = "ISOLATED",
}

export const FOUNDATION_SUB_SYSTEM_MATERIALS = {
  [FoundationSubSystemType.MONOLITHIC]: "Concrete",
  [FoundationSubSystemType.STEM_WALL_CMU]: "Concrete CMU",
  [FoundationSubSystemType.STEM_WALL_CIP]: "Concrete CIP",
  [FoundationSubSystemType.ISOLATED]: "Concrete",
} as const;

export const FOUNDATION_TYPES = {
  [FoundationSuperSystemType.SLAB_ON_GRADE]: [
    FoundationSubSystemType.MONOLITHIC,
    FoundationSubSystemType.STEM_WALL_CIP,
    FoundationSubSystemType.STEM_WALL_CMU,
    FoundationSubSystemType.ISOLATED,
  ] as const,
  [FoundationSuperSystemType.RAISED_SLAB]: [
    FoundationSubSystemType.STEM_WALL_CIP,
    FoundationSubSystemType.STEM_WALL_CMU,
    FoundationSubSystemType.ISOLATED,
  ] as const,
  [FoundationSuperSystemType.CRAWLSPACE]: [
    FoundationSubSystemType.STEM_WALL_CIP,
    FoundationSubSystemType.STEM_WALL_CMU,
    FoundationSubSystemType.ISOLATED,
  ] as const,
  [FoundationSuperSystemType.BASEMENT]: [
    FoundationSubSystemType.STEM_WALL_CIP,
    FoundationSubSystemType.STEM_WALL_CMU,
    FoundationSubSystemType.ISOLATED,
  ],
} as const;

type FoundationTypes = typeof FOUNDATION_TYPES;
export type FoundationSuperType = keyof FoundationTypes;
export type FoundationSubType<T extends FoundationSuperType> = FoundationTypes[T][number];

export enum FoundationSectionSizeType {
  VOID = "VOID",
  LINEAR = "LINEAR",
  ISOLATED = "ISOLATED",
}

export enum SlabType {
  CIP = "CIP",
  NONE = "NONE",
}

// Foundation Component Enums

export enum BrickLedgeBehavior {
  AUTO = "AUTO",
  FORCE = "FORCE",
}

export enum BrickLedgeType {
  NONE = "NONE",
  ADD = "ADD",
  INSET = "INSET",
}

export enum FootingType {
  TURNDOWN = "TURNDOWN",
  STRIP = "STRIP",
  CIRCULAR_ISOLATED = "CIRCULAR_ISOLATED",
  RECTANGULAR_ISOLATED = "RECTANGULAR_ISOLATED",
}

export enum PierType {
  CIRCULAR_CMU = "CIRCULAR_CMU",
  RECTANGULAR_CMU = "RECTANGULAR_CMU",
  CIRCULAR_CIP = "CIRCULAR_CIP",
  RECTANGULAR_CIP = "RECTANGULAR_CIP",
  CUSTOM = "CUSTOM",
}

export enum StemWallInsetType {
  NONE = "NONE",
  FULL_WIDTH = "FULL_WIDTH",
  MATCH_FRAMING = "MATCH_FRAMING",
  BY_VALUE = "BY_VALUE",
}

export enum StemWallType {
  CMU = "CMU",
  CIP = "CIP",
}

export enum CurbType {
  FIXED_WIDTH = "FIXED",
  AUTO_WIDTH = "AUTO",
}

// lexicographically sorted wrt FoundationRoomClass
export enum FoundationCondition {
  // house
  HOUSE_HOUSE = "HOUSE_HOUSE",
  HOUSE_GARAGE = "HOUSE_GARAGE",
  HOUSE_PORCH = "HOUSE_PORCH",
  HOUSE_BRICKLEDGE_PORCH = "HOUSE_BRICKLEDGE_PORCH",
  HOUSE_DECK = "HOUSE_DECK",
  HOUSE_BRICKLEDGE_DECK = "HOUSE_BRICKLEDGE_DECK",
  HOUSE_EXTERIOR = "HOUSE_EXTERIOR",
  HOUSE_BRICKLEDGE_EXTERIOR = "HOUSE_BRICKLEDGE_EXTERIOR",
  HOUSE_DISABLED = "HOUSE_DISABLED",
  // basement
  BASEMENT_BASEMENT = "BASEMENT_BASEMENT",
  BASEMENT_EXTERIOR = "BASEMENT_EXTERIOR",
  BASEMENT_BRICKLEDGE_EXTERIOR = "BASEMENT_BRICKLEDGE_EXTERIOR",
  BASEMENT_DISABLED = "BASEMENT_DISABLED",
  // garage
  GARAGE_GARAGE = "GARAGE_GARAGE",
  GARAGE_PORCH = "GARAGE_PORCH",
  GARAGE_BRICKLEDGE_PORCH = "GARAGE_BRICKLEDGE_PORCH",
  GARAGE_DECK = "GARAGE_DECK",
  GARAGE_BRICKLEDGE_DECK = "GARGE_BRICKLEDGE_DECK",
  GARAGE_EXTERIOR = "GARAGE_EXTERIOR",
  GARAGE_DOOR_EXTERIOR = "GARAGE_DOOR_EXTERIOR",
  GARAGE_BRICKLEDGE_EXTERIOR = "GARAGE_BRICKLEDGE_EXTERIOR",
  GARAGE_DISABLED = "GARAGE_DISABLED",
  // porch
  PORCH_PORCH = "PORCH_PORCH",
  PORCH_DECK = "PORCH_DECK",
  PORCH_BRICKLEDGE_DECK = "PORCH_BRICKLEDGE_DECK",
  PORCH_EXTERIOR = "PORCH_EXTERIOR",
  PORCH_BRICKLEDGE_EXTERIOR = "PORCH_BRICKLEDGE_EXTERIOR",
  PORCH_DISABLED = "PORCH_DISABLED",
  // deck
  DECK_DECK = "DECK_DECK",
  DECK_EXTERIOR = "DECK_EXTERIOR",
  DECK_DISABLED = "DECK_DISABLED",
  // exterior
  EXTERIOR_EXTERIOR = "EXTERIOR_EXTERIOR",
}
